/**
 * MobileSecurity — device sessions + remote wipe (Phase 1, remote-wipe FRONTEND).
 *
 * Lists the caller's device_sessions (own-or-admin via the table's SELECT RLS: a normal user sees
 * ONLY their own rows; an admin/superadmin sees every device). Each row shows platform/model, the
 * OS + app version, last-seen time and status, plus a Revoke (remote-wipe) button.
 *
 * Revoke calls the `revoke-device-session` edge function ({ wiped: true } on success). That function
 * marks the row wiped, force-signs-out the target, and fires a silent "wipe" push so the device
 * purges itself on next connect. Revoke is a LIVE write, so it is disabled when the row is already
 * inactive (status !== 'active') OR when this device is offline (`useIsOnline()` === false) — there
 * is no safe offline queueing for a security action.
 *
 * The list READ is offline-aware via `useCachedQuery` (cacheKey "mobile.device_sessions"): the
 * queryFn THROWS on `{ error }` so React Query keeps the last-known-good cached placeholder (and
 * serves it offline) instead of overwriting it with an empty/partial value. The current device
 * (matched against the locally-persisted device_session_id from `getStoredDeviceSessionId()`) is
 * tagged with a "This device" badge so the user can tell which row wipes the phone in their hand.
 *
 * The Supabase generated types are stale for device_sessions, so the `.from(...)` and
 * `.functions.invoke(...)` calls are cast through `any`. Web/desktop are unaffected: this is a
 * RLS-safe read reachable from the mobile Security screen; nothing here is native-only behaviour.
 */
import { useEffect, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { ShieldCheck, Smartphone, Monitor, Loader2, WifiOff } from 'lucide-react';
import { useIsOnline } from '@/hooks/useIsOnline';
import { useCachedQuery } from '@/lib/offline/cachedQuery';
import { getStoredDeviceSessionId } from '@/lib/native/deviceSession';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Shapes
// ---------------------------------------------------------------------------

interface DeviceSessionRow {
  id: string;
  user_id: string;
  device_id: string | null;
  platform: string | null;
  model: string | null;
  os_version: string | null;
  app_version: string | null;
  status: string | null; // "active" | "wiped"
  last_seen_at: string | null;
  created_at: string | null;
  wiped_at: string | null;
}

// ---------------------------------------------------------------------------
// Fetch
// ---------------------------------------------------------------------------

async function fetchDeviceSessions(): Promise<DeviceSessionRow[]> {
  // RLS gates this to own-or-admin: a normal user gets only their rows, admins get all. We do NOT
  // filter by user_id here so an admin sees every device; the policy does the scoping.
  const res = await (supabase as any)
    .from('device_sessions')
    .select(
      'id, user_id, device_id, platform, model, os_version, app_version, status, last_seen_at, created_at, wiped_at',
    )
    .order('last_seen_at', { ascending: false, nullsFirst: false });

  // Throw on error so useCachedQuery keeps the cached placeholder instead of caching an empty list.
  if (res.error) throw res.error;
  return (Array.isArray(res.data) ? res.data : []) as DeviceSessionRow[];
}

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

function deviceLabel(row: DeviceSessionRow): string {
  const model = row.model?.trim();
  if (model) return model;
  const platform = row.platform?.trim();
  if (platform) return platform.charAt(0).toUpperCase() + platform.slice(1);
  return 'Unknown device';
}

function platformLine(row: DeviceSessionRow): string {
  const parts: string[] = [];
  const platform = row.platform?.trim();
  if (platform) {
    const os = row.os_version?.trim();
    parts.push(os ? `${platform} ${os}` : platform);
  } else if (row.os_version?.trim()) {
    parts.push(row.os_version.trim());
  }
  if (row.app_version?.trim()) parts.push(`App ${row.app_version.trim()}`);
  return parts.join(' · ');
}

function lastSeenLabel(iso: string | null): string | null {
  if (!iso) return null;
  try {
    return `Last seen ${formatDistanceToNow(new Date(iso), { addSuffix: true })}`;
  } catch {
    return null;
  }
}

function isApplePlatform(platform: string | null): boolean {
  return (platform ?? '').toLowerCase() === 'ios';
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function MobileSecurity() {
  const online = useIsOnline();
  const { toast } = useToast();

  // Locally-persisted id of THIS device's session (set by deviceSession.ts on login). Used only to
  // badge the matching row; read once on mount (it does not change during a session).
  const [thisDeviceId, setThisDeviceId] = useState<string | null>(null);
  // Id of the row whose revoke request is currently in flight (per-row spinner + disable).
  const [revokingId, setRevokingId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const id = await getStoredDeviceSessionId();
        if (!cancelled) setThisDeviceId(id ?? null);
      } catch {
        // Best-effort: no "This device" badge if the local id can't be read. Never blocks the screen.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const sessions = useCachedQuery<DeviceSessionRow[]>({
    queryKey: ['mobile-device-sessions'],
    cacheKey: 'mobile.device_sessions',
    queryFn: fetchDeviceSessions,
  });

  const rows = sessions.data ?? [];

  // "Showing saved data" affordance when serving last-known-good (offline or pre-fetch).
  const showStale = sessions.isFromCache && sessions.cachedAt != null;
  let staleLabel: string | null = null;
  if (showStale && sessions.cachedAt) {
    try {
      staleLabel = `Saved ${formatDistanceToNow(new Date(sessions.cachedAt), { addSuffix: true })}`;
    } catch {
      staleLabel = 'Showing saved data';
    }
  }

  // Hard error only when we have nothing at all to show (no cache to fall back to).
  const hardError = sessions.isError && !sessions.isFromCache && sessions.data === undefined;
  const loading = sessions.isLoading && !sessions.isFromCache && sessions.data === undefined;

  const handleRevoke = async (row: DeviceSessionRow) => {
    if (!online || row.status !== 'active' || revokingId) return;

    const isThis = thisDeviceId != null && row.id === thisDeviceId;
    setRevokingId(row.id);
    try {
      const { data, error } = await (supabase.functions.invoke as any)('revoke-device-session', {
        body: { device_session_id: row.id, reason: 'user_revoked' },
      });
      // The edge fn returns { wiped: true } on success; a thrown error or a non-wiped body is a failure.
      if (error || !data || data.wiped !== true) {
        throw error ?? new Error('Revoke did not complete');
      }

      toast({
        title: 'Device revoked',
        description: isThis
          ? 'This device has been wiped and signed out.'
          : `${deviceLabel(row)} has been wiped and signed out.`,
      });

      // Refetch so the row flips to "wiped" (and the button disables). If this was the current
      // device, the silent wipe push will sign us out shortly; the refetch is still harmless.
      await sessions.refetch();
    } catch {
      toast({
        title: "Couldn't revoke device",
        description: online
          ? 'The wipe request failed. Check your connection and try again.'
          : "You're offline — revoking a device needs a connection.",
        variant: 'destructive',
      });
    } finally {
      setRevokingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-background px-4 pb-8 pt-[calc(1.25rem+env(safe-area-inset-top))]">
      <header className="mb-4">
        <h1 className="font-heading text-2xl font-semibold text-primary">Security &amp; devices</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Devices signed in to your account. Revoke any you don&rsquo;t recognise to wipe and sign it out.
        </p>
        {showStale && (
          <p
            className={cn(
              'mt-1 text-[11px] font-medium uppercase tracking-wide',
              online ? 'text-muted-foreground/70' : 'text-amber-600',
            )}
          >
            {staleLabel}
          </p>
        )}
        {!online && (
          <p className="mt-2 flex items-center gap-1.5 text-xs text-amber-600">
            <WifiOff className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            Offline — revoking a device needs a connection.
          </p>
        )}
      </header>

      <div className="space-y-3">
        {loading ? (
          <section className="flex items-center justify-center rounded-xl border border-border bg-card p-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" aria-hidden="true" />
            <span className="sr-only">Loading devices</span>
          </section>
        ) : hardError ? (
          <section className="rounded-xl border border-border bg-card p-4">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground/70">Couldn&rsquo;t load</p>
            <p className="mt-2 text-sm text-muted-foreground">
              {online
                ? 'We couldn’t load your devices just now. Pull back in a moment.'
                : 'You’re offline and there’s no saved device list yet. This will fill in once you reconnect.'}
            </p>
          </section>
        ) : rows.length === 0 ? (
          <section className="rounded-xl border border-border bg-card p-4">
            <div className="flex items-center gap-2">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <ShieldCheck className="h-5 w-5" aria-hidden="true" />
              </span>
              <h2 className="font-heading text-sm font-semibold">No devices yet</h2>
            </div>
            <p className="mt-2 text-sm text-muted-foreground">
              Devices appear here once you sign in to the mobile app on them.
            </p>
          </section>
        ) : (
          rows.map((row) => {
            const isThis = thisDeviceId != null && row.id === thisDeviceId;
            const active = row.status === 'active';
            const revoking = revokingId === row.id;
            const canRevoke = active && online && !revoking && revokingId === null;
            const Icon = isApplePlatform(row.platform) || row.model ? Smartphone : Monitor;
            const subline = platformLine(row);
            const seen = lastSeenLabel(row.last_seen_at);

            return (
              <section key={row.id} className="rounded-xl border border-border bg-card p-4">
                <div className="flex items-start gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <Icon className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="truncate font-heading text-sm font-semibold">{deviceLabel(row)}</h2>
                      {isThis && (
                        <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-primary">
                          This device
                        </span>
                      )}
                      {!active && (
                        <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                          Revoked
                        </span>
                      )}
                    </div>
                    {subline && (
                      <p className="mt-0.5 truncate text-xs text-muted-foreground">{subline}</p>
                    )}
                    {seen && (
                      <p className="mt-0.5 text-[11px] uppercase tracking-wide text-muted-foreground/70">
                        {seen}
                      </p>
                    )}
                  </div>
                </div>

                <div className="mt-3 flex justify-end">
                  <button
                    type="button"
                    onClick={() => handleRevoke(row)}
                    disabled={!canRevoke}
                    aria-disabled={!canRevoke}
                    aria-busy={revoking}
                    className={cn(
                      'inline-flex items-center justify-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors',
                      active
                        ? 'border-destructive/40 text-destructive hover:bg-destructive/10'
                        : 'border-border text-muted-foreground',
                      'disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent',
                    )}
                  >
                    {revoking && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />}
                    {active ? (revoking ? 'Revoking…' : 'Revoke') : 'Revoked'}
                  </button>
                </div>
              </section>
            );
          })
        )}
      </div>
    </div>
  );
}
